import { Checkbox } from '../../../../../../widgets/checkbox';
import { Dialog } from '../../../../../../widgets/dialog';
import { Icon } from '../../../../../../widgets/icon';
import React from 'react';
import { T } from 'src/shared/helpers';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { ValidationBox } from '../../../../../../widgets/validation/validationBox';
import { ComposerAttributeNode, ComposerAttributeOptionNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import Stack from '../../../../../../widgets/stack/stack';
import ExpandLessIcon from 'src/shared/material_icons/navigation/expand_less.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import {
    ComposerAttributeSelectionGrid,
    SelectionFooter,
    SelectionFooterBasicButton,
    SelectionFooterPrimaryButton,
    SelectionGrid,
    SelectionHeader,
    SelectionItemName,
    selectionSurface,
    toggleSetItem
} from './composerSelectionLayout';

interface ComposerCopyOptionsAttributeOutput {
    optionsToCopy: ComposerAttributeOptionNode[];
    targetAttributes: ComposerAttributeNode[];
    replaceExistingOptions: boolean;
}
interface ComposerCopyOptionsAttributeDialogProps {
    attributesList: ComposerAttributeNode[];
    onConfirm: (attributes: ComposerCopyOptionsAttributeOutput) => void;
}

type CopyOptionsStep = 'options' | 'targets';

const OptionsCard = styled.div<{ $selected: boolean }>`
    ${selectionSurface}
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const OptionsCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 48px;
    padding: 8px 12px 8px 16px;
    cursor: pointer;
    user-select: none;

    > ${SelectionItemName} {
        flex: 1;
    }
`;

const OptionsCount = styled.span`
    font-size: 12px;
    color: ${(props) => props.theme.app.primary};
`;

const OptionsList = styled.div`
    display: flex;
    flex-direction: column;
    padding: 4px 0;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
`;

const OptionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 36px;
    padding: 0 6px 0 16px;
    cursor: pointer;

    &:hover {
        background: ${(props) => props.theme.app.backgroundToolbar};
    }
`;

const ComposerCopyOptionsAttributeDialogContent: React.FC<ComposerCopyOptionsAttributeDialogProps> = ({ attributesList, onConfirm }) => {
    const validationContext = useValidationContext();
    const [step, setStep] = React.useState<CopyOptionsStep>('options');
    const [selectedOptionIds, setSelectedOptionIds] = React.useState<Set<string>>(new Set());
    const [expandedAttributeIds, setExpandedAttributeIds] = React.useState<Set<string>>(new Set());
    const [targetAttributeIds, setTargetAttributeIds] = React.useState<Set<string>>(new Set());
    const [replaceExistingOptions, setReplaceExistingOptions] = React.useState<boolean>(false);

    const sourceAttributes = attributesList.filter((attr) => attr.options.length > 0);
    const allOptionIds = sourceAttributes.flatMap((attr) => attr.options.map((opt) => opt.id));
    const allOptionsSelected = selectedOptionIds.size === allOptionIds.length;

    const toggleOption = (optionID: string, selected: boolean) => setSelectedOptionIds((prev) => toggleSetItem(prev, optionID, selected));
    const toggleExpanded = (attributeID: string) => setExpandedAttributeIds((prev) => toggleSetItem(prev, attributeID, !prev.has(attributeID)));

    const goToStep = (nextStep: CopyOptionsStep) => {
        setStep(nextStep);
        validationContext.resetSubmitted();
    };

    const handleNext = () => {
        if (!validationContext.submit()) return;
        goToStep('targets');
    };

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        onConfirm({
            optionsToCopy: sourceAttributes.flatMap((attr) => attr.options).filter((opt) => selectedOptionIds.has(opt.id)),
            targetAttributes: attributesList.filter((attr) => targetAttributeIds.has(attr.id)),
            replaceExistingOptions
        });
    };

    if (step === 'targets') {
        return (
            <Dialog
                size='medium'
                title={T._('Selection of target attributes', 'Admin')}
                footerContent={
                    <SelectionFooter>
                        <Checkbox
                            label={T._('Replace existing options in the target attribute(s) with the new ones', 'Admin')}
                            checked={replaceExistingOptions}
                            onChange={setReplaceExistingOptions}
                        />
                        <SelectionFooterBasicButton onClick={() => goToStep('options')}>{T._('Back', 'Admin')}</SelectionFooterBasicButton>
                        <SelectionFooterPrimaryButton onClick={handleConfirm}>{T._('Confirm', 'Admin')}</SelectionFooterPrimaryButton>
                    </SelectionFooter>
                }
            >
                <Stack>
                    <SelectionHeader>
                        <p>{T._('Please select below the attribute(s) in which you want to copy the options', 'Admin')}</p>
                    </SelectionHeader>
                    <ValidationBox
                        fullWidth
                        validationKey='composerCopyTargetAttributes'
                        validationLabel={T._('Target attributes', 'Admin')}
                        validationValue={targetAttributeIds}
                        validate={(value) => value.size === 0}
                        validationMessage={T._('At least one target attribute is required.', 'Admin')}
                    >
                        <ComposerAttributeSelectionGrid
                            attributes={attributesList}
                            selectedIds={targetAttributeIds}
                            onChange={(attributeID, selected) => setTargetAttributeIds((prev) => toggleSetItem(prev, attributeID, selected))}
                        />
                    </ValidationBox>
                </Stack>
            </Dialog>
        );
    }

    return (
        <Dialog
            size='medium'
            title={T._('Selection of options to copy', 'Admin')}
            footerContent={
                <SelectionFooter>
                    <SelectionFooterPrimaryButton onClick={handleNext}>{T._('Next', 'Admin')}</SelectionFooterPrimaryButton>
                </SelectionFooter>
            }
        >
            <Stack>
                <SelectionHeader>
                    <p>{T._('Please select below the options you wish to duplicate into other attribute(s).', 'Admin')}</p>
                    <Checkbox
                        label={T._('Select all', 'Admin')}
                        checked={selectedOptionIds.size > 0}
                        indeterminate={selectedOptionIds.size > 0 && !allOptionsSelected}
                        onChange={() => setSelectedOptionIds(allOptionsSelected ? new Set() : new Set(allOptionIds))}
                    />
                </SelectionHeader>
                <ValidationBox
                    fullWidth
                    validationKey='composerCopyOptions'
                    validationLabel={T._('Options to copy', 'Admin')}
                    validationValue={selectedOptionIds}
                    validate={(value) => value.size === 0}
                    validationMessage={T._('At least one option to copy is required.', 'Admin')}
                >
                    <SelectionGrid>
                        {sourceAttributes.map((attr) => {
                            const expanded = expandedAttributeIds.has(attr.id);
                            const selectedCount = attr.options.filter((opt) => selectedOptionIds.has(opt.id)).length;
                            return (
                                <OptionsCard key={attr.id} $selected={selectedCount > 0}>
                                    <OptionsCardHeader
                                        role='button'
                                        tabIndex={0}
                                        aria-expanded={expanded}
                                        onClick={() => toggleExpanded(attr.id)}
                                        onKeyDown={(e) => {
                                            if (e.key !== 'Enter' && e.key !== ' ') return;
                                            e.preventDefault();
                                            toggleExpanded(attr.id);
                                        }}
                                    >
                                        <SelectionItemName $bold title={attr.name}>
                                            {attr.name}
                                        </SelectionItemName>
                                        <OptionsCount>
                                            {selectedCount}/{attr.options.length}
                                        </OptionsCount>
                                        <Icon>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Icon>
                                    </OptionsCardHeader>
                                    {expanded && (
                                        <OptionsList>
                                            {attr.options.map((opt) => {
                                                const selected = selectedOptionIds.has(opt.id);
                                                return (
                                                    <OptionRow key={opt.id} onClick={() => toggleOption(opt.id, !selected)}>
                                                        <SelectionItemName title={opt.name}>{opt.name}</SelectionItemName>
                                                        <Checkbox checked={selected} onChange={(checked) => toggleOption(opt.id, checked)} />
                                                    </OptionRow>
                                                );
                                            })}
                                        </OptionsList>
                                    )}
                                </OptionsCard>
                            );
                        })}
                    </SelectionGrid>
                </ValidationBox>
            </Stack>
        </Dialog>
    );
};

export const ComposerCopyOptionsAttributeDialog: React.FC<ComposerCopyOptionsAttributeDialogProps> = (props) => (
    <ValidationStateProvider>
        <ComposerCopyOptionsAttributeDialogContent {...props} />
    </ValidationStateProvider>
);
